import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import ErrorBoundary from './components/ErrorBoundary';
import Dashboard from './components/Dashboard';
import Clientes from './components/Clientes';
import Consultas from './components/Consultas';
import ResumenSesiones from './components/ResumenSesiones';
import Citas from './components/Citas';
import Documentos from './components/Documentos';
import Facturacion from './components/Facturacion';
import Oportunidades from './components/Oportunidades';
import Tareas from './components/Tareas';
import Reportes from './components/Reportes';
import Historico from './components/Historico';
import Plantillas from './components/Plantillas';
import Usuarios from './components/Usuarios';
import Suscripciones from './components/Suscripciones';
import Backups from './components/Backups';
import AuditoriaGeneral from './components/AuditoriaGeneral';
import Perfil from './components/Perfil';
import Integraciones from './components/Integraciones';
import Interacciones from './components/Interacciones';
import SearchBar from './components/SearchBar';
import NotificacionesBell from './components/NotificacionesBell';
import ScrollToTopButton from './components/ScrollToTopButton';
import Landing from './components/Landing';
import ReservaPublica from './components/ReservaPublica';
import ReservaPublicaHorizontal from './components/ReservaPublicaHorizontal';
import PortalCliente from './components/PortalCliente';
import VideoLlamada from './components/VideoLlamada';
import DisponibilidadConfig from './components/DisponibilidadConfig';
import { authAPI, recordatoriosAPI, facturasRecurrentesAPI, configuracionAPI } from './services/api';
import Mantenimiento from './components/Mantenimiento';
import PoliticaPrivacidad from './components/PoliticaPrivacidad';
import TerminosCondiciones from './components/TerminosCondiciones';
import { VERSION } from './version';
import { getLabels } from './utils/userLabels';
import './App.css';

function NavDropdown({ label, linkTo, items, navbarTextColor, setMenuOpen, location, dropdownOnly }) {
  const [open, setOpen] = useState(false);
  const timeoutRef = useRef(null);
  const hasItems = items && items.length > 0;

  useEffect(() => {
    return () => clearTimeout(timeoutRef.current);
  }, []);

  const handleEnter = () => {
    if (!hasItems) return;
    clearTimeout(timeoutRef.current);
    setOpen(true);
  };
  const handleLeave = () => {
    timeoutRef.current = setTimeout(() => setOpen(false), 150);
  };

  const isActive = location.pathname === linkTo || (hasItems && items.some(item => location.pathname === item.to));

  return (
    <div
      className="nav-dropdown"
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      {hasItems && dropdownOnly ? (
        <span
          className={`nav-dropdown-trigger ${isActive ? 'active' : ''}`}
          style={{ color: navbarTextColor, cursor: 'pointer' }}
          onClick={() => { setOpen(prev => !prev); }}
        >
          {label}
          <span className="nav-caret">▾</span>
        </span>
      ) : (
        <Link
          to={linkTo}
          className={`nav-dropdown-trigger ${isActive ? 'active' : ''}`}
          style={{ color: navbarTextColor }}
          onClick={() => { setMenuOpen(false); }}
        >
          {label}
          {hasItems && <span className="nav-caret">▾</span>}
        </Link>
      )}
      {hasItems && open && (
        <>
          <div className="nav-dropdown-bridge"></div>
          <div
            className="nav-dropdown-menu"
            onMouseEnter={handleEnter}
            onMouseLeave={handleLeave}
          >
            {items.map(item => (
              <Link
                key={item.to}
                to={item.to}
                className={location.pathname === item.to ? 'active' : ''}
                onClick={() => { setOpen(false); setMenuOpen(false); }}
              >
                {item.icon && <span className="nav-dropdown-icon">{item.icon}</span>}
                {item.label}
              </Link>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function Navbar() {
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    // Función para actualizar el favicon
    const updateFavicon = (faviconPath) => {
      // Eliminar favicons existentes
      const existingLinks = document.querySelectorAll("link[rel*='icon']");
      existingLinks.forEach(link => link.remove());
      
      // Crear nuevo link para el favicon
      const link = document.createElement('link');
      link.rel = 'icon';
      link.type = faviconPath.endsWith('.svg') ? 'image/svg+xml' : 
                  faviconPath.endsWith('.png') ? 'image/png' : 
                  'image/x-icon';
      link.href = `/${faviconPath}`;
      document.getElementsByTagName('head')[0].appendChild(link);
    };

    // Verificar estado de autenticación al cargar
    authAPI.check()
      .then(response => {
        if (response.data.authenticated) {
          setUser(response.data.user);
          // Disparar recordatorios automáticos (24 h antes) si el coach los tiene activados. Sin cron.
          recordatoriosAPI.run().catch(() => {});
          // Generar facturas recurrentes pendientes (mensual, trimestral, etc.)
          facturasRecurrentesAPI.runJob().catch(() => {});
          // Actualizar favicon si el usuario tiene uno
          if (response.data.user.favicon) {
            updateFavicon(response.data.user.favicon);
          } else {
            // Usar favicon genérico si no tiene uno personalizado
            // El favicon.ico debe estar en la raíz del servidor o en frontend/build/
            updateFavicon('favicon.ico');
          }
        } else {
          // Si no está autenticado, redirigir al login
          window.location.href = '/login.php';
        }
      })
      .catch(() => {
        // Si hay error, redirigir al login
        window.location.href = '/login.php';
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const handleLogout = async () => {
    try {
      await authAPI.logout();
      // Redirigir al login después del logout
      window.location.href = '/login.php';
    } catch (error) {
      console.error('Error al hacer logout:', error);
      // Aún así redirigir al login
      window.location.href = '/login.php';
    }
  };

  // Obtener colores personalizados del usuario
  const navbarBgColor = user?.navbar_background_color || '#2c3e50';
  const navbarTextColor = user?.navbar_text_color || '#ffffff';
  const labels = getLabels(user?.tipo_usuario || 'coach');

  if (loading) {
    return (
      <nav className="navbar" style={{ backgroundColor: navbarBgColor, color: navbarTextColor }}>
        <div className="navbar-content">
          <div className="navbar-brand-logo">
            <div
              aria-hidden="true"
              style={{ width: '180px', height: '60px' }}
            />
          </div>
        </div>
      </nav>
    );
  }

  return (
    <nav className="navbar" style={{ backgroundColor: navbarBgColor, color: navbarTextColor }}>
      <div className="navbar-content">
        {user && user.logo ? (
          <Link to="/" className="navbar-logo">
            <img 
              src={`/${user.logo}`} 
              alt="Logo" 
            />
          </Link>
        ) : (
          <Link to="/" className="navbar-brand-logo" style={{ textDecoration: 'none', color: 'inherit' }}>
            <img 
              src="/coachflow-logo.png" 
              alt="COACHFLOW" 
            />
          </Link>
        )}
        <button 
          className="hamburger-menu"
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
          style={{ color: navbarTextColor }}
        >
          <span style={{ backgroundColor: navbarTextColor }}></span>
          <span style={{ backgroundColor: navbarTextColor }}></span>
          <span style={{ backgroundColor: navbarTextColor }}></span>
        </button>
        <div className={`nav-links ${menuOpen ? 'nav-links-open' : ''}`}>
          {user && user.rol === 'admin' ? (
            <>
              <NavDropdown label="Dashboard" linkTo="/" navbarTextColor={navbarTextColor} setMenuOpen={setMenuOpen} location={location} />
              <NavDropdown
                label="Facturación"
                linkTo="/facturacion"
                items={[
                  { to: '/suscripciones', label: 'Suscripciones', icon: '💳' },
                ]}
                navbarTextColor={navbarTextColor}
                setMenuOpen={setMenuOpen}
                location={location}
              />
              <NavDropdown
                label="Herramientas"
                linkTo="/herramientas"
                dropdownOnly
                items={[
                  { to: '/backups', label: 'Backups', icon: '💾' },
                  { to: '/usuarios', label: 'Usuarios', icon: '👥' },
                  { to: '/auditoria', label: 'Auditoría', icon: '📋' },
                ]}
                navbarTextColor={navbarTextColor}
                setMenuOpen={setMenuOpen}
                location={location}
              />
            </>
          ) : (
            <>
              <NavDropdown label="Dashboard" linkTo="/" navbarTextColor={navbarTextColor} setMenuOpen={setMenuOpen} location={location} />
              <NavDropdown
                label="Clientes"
                linkTo="/clientes"
                items={[
                  { to: '/interacciones', label: 'Interacciones', icon: '🔗' },
                ]}
                navbarTextColor={navbarTextColor}
                setMenuOpen={setMenuOpen}
                location={location}
              />
              <NavDropdown
                label="Agenda"
                linkTo="/agenda"
                items={
                  user && (user.disponibilidad_habilitada === true || user.disponibilidad_habilitada === 1)
                    ? [{ to: '/disponibilidad', label: 'Disponibilidad', icon: '🗓️' }]
                    : []
                }
                navbarTextColor={navbarTextColor}
                setMenuOpen={setMenuOpen}
                location={location}
              />
              {user && (user.oportunidades_habilitadas === true || user.oportunidades_habilitadas === 1) && (
                <NavDropdown label="Oportunidades" linkTo="/oportunidades" navbarTextColor={navbarTextColor} setMenuOpen={setMenuOpen} location={location} />
              )}
              <NavDropdown
                label={labels.sesiones}
                linkTo="/sesiones"
                items={[
                  { to: '/sesiones/resumen', label: labels.resumen_sesiones, icon: '📈' },
                ]}
                navbarTextColor={navbarTextColor}
                setMenuOpen={setMenuOpen}
                location={location}
              />
              {user && (user.tareas_habilitadas === true || user.tareas_habilitadas === 1) && (
                <NavDropdown label="Tareas" linkTo="/tareas" navbarTextColor={navbarTextColor} setMenuOpen={setMenuOpen} location={location} />
              )}
              <NavDropdown
                label="Documentos"
                linkTo="/documentos"
                items={[
                  { to: '/plantillas', label: 'Plantillas', icon: '📋' },
                ]}
                navbarTextColor={navbarTextColor}
                setMenuOpen={setMenuOpen}
                location={location}
              />
              {user && ((user.facturacion_habilitada === true || user.facturacion_habilitada === 1) || 
                        (user.oportunidades_habilitadas === true || user.oportunidades_habilitadas === 1)) && (
                <NavDropdown label="Reportes" linkTo="/reportes" navbarTextColor={navbarTextColor} setMenuOpen={setMenuOpen} location={location} />
              )}
              {user && (user.facturacion_habilitada === true || user.facturacion_habilitada === 1) && (
                <NavDropdown label="Facturas" linkTo="/facturacion" navbarTextColor={navbarTextColor} setMenuOpen={setMenuOpen} location={location} />
              )}
              {user && (user.integraciones_habilitadas === true || user.integraciones_habilitadas === 1) && (
                <NavDropdown label="Integraciones" linkTo="/integraciones" navbarTextColor={navbarTextColor} setMenuOpen={setMenuOpen} location={location} />
              )}
              <NavDropdown label="Histórico" linkTo="/historico" navbarTextColor={navbarTextColor} setMenuOpen={setMenuOpen} location={location} />
            </>
          )}
        </div>
        <SearchBar searchPlaceholder={labels.buscar_placeholder} sesionesLabel={labels.sesiones_lower} />
        <div className="navbar-icons" style={{ color: navbarTextColor }}>
          {user && (
            <>
              {(user.rol === 'admin' || user.notificaciones_habilitadas === 1 || user.notificaciones_habilitadas === true) && (
                <NotificacionesBell navbarTextColor={navbarTextColor} />
              )}
              <Link to="/perfil" title="Mi Perfil" onClick={() => setMenuOpen(false)} style={{ color: navbarTextColor }} className={`navbar-icon ${location.pathname === '/perfil' ? 'active' : ''}`}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
              </Link>
              <button className="navbar-icon btn-logout-icon" onClick={handleLogout} title="Cerrar sesión" style={{ color: navbarTextColor }}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>
              </button>
            </>
          )}
        </div>
      </div>
      {menuOpen && <div className="nav-overlay" onClick={() => setMenuOpen(false)}></div>}
    </nav>
  );
}

function ProtectedRoutes() {
  const location = useLocation();
  const [user, setUser] = useState(null);

  useEffect(() => {
    authAPI.check()
      .then(response => {
        if (response.data.authenticated) {
          setUser(response.data.user);
        }
      })
      .catch(() => {
        // Ignorar errores, el Navbar ya maneja la redirección
      });
  }, []);

  const navbarBgColor = user?.navbar_background_color || '#2c3e50';
  const navbarTextColor = user?.navbar_text_color || '#ffffff';

  return (
    <>
      <Navbar />
      <div className="container">
        <ErrorBoundary>
          <div key={`${location.pathname}${location.search}`} className="route-transition">
            <Routes location={location}>
              <Route index element={<Dashboard />} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="agenda" element={<Citas />} />
              <Route path="sesiones" element={<Consultas />} />
              <Route path="sesiones/resumen" element={<ResumenSesiones />} />
              <Route path="documentos" element={<Documentos />} />
              <Route path="facturacion" element={<Facturacion />} />
              <Route path="oportunidades" element={<Oportunidades />} />
              <Route path="tareas" element={<Tareas />} />
              <Route path="reportes" element={<Reportes />} />
              <Route path="historico" element={<Historico />} />
              <Route path="clientes" element={<Clientes />} />
              <Route path="interacciones" element={<Interacciones />} />
              <Route path="plantillas" element={<Plantillas />} />
              <Route path="herramientas" element={<Usuarios />} />
              <Route path="usuarios" element={<Usuarios />} />
              <Route path="suscripciones" element={<Suscripciones />} />
              <Route path="backups" element={<Backups />} />
              <Route path="auditoria" element={<AuditoriaGeneral />} />
              <Route path="integraciones" element={<Integraciones />} />
              <Route path="perfil" element={<Perfil />} />
              <Route path="disponibilidad" element={<DisponibilidadConfig />} />
              <Route path="*" element={<div style={{textAlign:'center',padding:'3rem'}}><h2>Página no encontrada</h2><p><Link to="/">Volver al inicio</Link></p></div>} />
            </Routes>
          </div>
        </ErrorBoundary>
      </div>
      <footer className="app-footer" style={{ backgroundColor: navbarBgColor, color: navbarTextColor }}>
        <p>
          &copy; 2025-26 CdL. All rights reserved. | Made with Love in Spain | v{VERSION}
          <span className="footer-links">
            {' | '}<Link to="/privacidad" style={{ color: 'inherit', opacity: 0.8 }}>Política de Privacidad</Link>
            {' | '}<Link to="/terminos" style={{ color: 'inherit', opacity: 0.8 }}>Términos y Condiciones</Link>
          </span>
        </p>
      </footer>
      <ScrollToTopButton />
    </>
  );
}

function HomeRoute() {
  const [loading, setLoading] = useState(true);
  const [authenticated, setAuthenticated] = useState(false);
  const [user, setUser] = useState(null);

  useEffect(() => {
    // Verificar estado de autenticación al cargar
    authAPI.check()
      .then(response => {
        setAuthenticated(response.data.authenticated);
        if (response.data.authenticated) {
          setUser(response.data.user);
        }
        setLoading(false);
      })
      .catch(() => {
        setAuthenticated(false);
        setLoading(false);
      });
  }, []);

  if (loading) {
    return <div className="loading">Cargando...</div>;
  }

  // Si está autenticado, mostrar dashboard con navbar (sin cambiar la URL)
  if (authenticated) {
    const navbarBgColor = user?.navbar_background_color || '#2c3e50';
    const navbarTextColor = user?.navbar_text_color || '#ffffff';

    return (
      <>
        <Navbar />
        <div className="container">
          <Dashboard />
        </div>
        <footer className="app-footer" style={{ backgroundColor: navbarBgColor, color: navbarTextColor }}>
          <p>
            &copy; 2025-26 CdL. All rights reserved. | Made with Love in Spain | v{VERSION}
            <span className="footer-links">
              {' | '}<Link to="/privacidad" style={{ color: 'inherit', opacity: 0.8 }}>Política de Privacidad</Link>
              {' | '}<Link to="/terminos" style={{ color: 'inherit', opacity: 0.8 }}>Términos y Condiciones</Link>
            </span>
          </p>
        </footer>
        <ScrollToTopButton />
      </>
    );
  }

  // Si no está autenticado, mostrar Landing
  return <Landing />;
}

function AppBootstrap() {
  const [loading, setLoading] = useState(true);
  const [auth, setAuth] = useState(null);
  const [modoMantenimiento, setModoMantenimiento] = useState(false);

  useEffect(() => {
    Promise.all([
      authAPI.check().catch(() => ({ data: { authenticated: false } })),
      configuracionAPI.getModoMantenimiento().catch(() => ({ data: { modo_mantenimiento: 0 } })),
    ]).then(([authRes, configRes]) => {
      setAuth(authRes.data);
      setModoMantenimiento((configRes.data?.modo_mantenimiento ?? 0) === 1);
      setLoading(false);
    }).catch(() => {
      setAuth({ authenticated: false });
      setModoMantenimiento(false);
      setLoading(false);
    });
  }, []);

  if (loading) {
    return <div className="loading">Cargando...</div>;
  }
  const isAdmin = auth?.authenticated && auth?.user?.rol === 'admin';
  if (modoMantenimiento && !isAdmin) {
    return <Mantenimiento />;
  }
  return (
    <Router>
      <div className="App">
        <ErrorBoundary>
          <Routes>
            <Route path="/" element={<HomeRoute />} />
            <Route path="/landing" element={<Landing />} />
            <Route path="/reservar/:username" element={<ReservaPublica />} />
            <Route path="/booking/:username" element={<ReservaPublicaHorizontal />} />
            <Route path="/portal/:token" element={<PortalCliente />} />
            <Route path="/videollamada/:token" element={<VideoLlamada />} />
            <Route path="/privacidad" element={<PoliticaPrivacidad />} />
            <Route path="/terminos" element={<TerminosCondiciones />} />
            <Route path="/*" element={<ProtectedRoutes />} />
          </Routes>
        </ErrorBoundary>
      </div>
    </Router>
  );
}

function App() {
  return <AppBootstrap />;
}

export default App;
